import { Component, ElementRef, Input, OnInit } from '@angular/core'
import { RoutingService, TranslationService } from '@spartacus/core'
import { MediaContainer } from '@spartacus/storefront'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { EbSelectBoxItem } from '../../../../components/select-box/eb-select-box.interface'
import { EbFacet, EbFacetValue } from '../../interfaces/eb-product.interface'
import { EbFacetService } from '../product-facet-navigation/facet/eb-facet.service'

export declare type EbSelectConfig = {
  code: string
  label: string
  items: EbSelectBoxItem[]
  specialFacet?: boolean
  images?: { [key: string]: MediaContainer }
  selectedValue?: EbSelectBoxItem
}

@Component({
    selector: 'eb-product-variants-filter',
    templateUrl: './eb-product-variants-filter.component.html',
    styleUrls: ['./eb-product-variants-filter.component.scss'],
    standalone: false
})
export class EbProductVariantsFilterComponent implements OnInit {
  constructor(
    private readonly elementRef: ElementRef,
    protected translationService: TranslationService,
    public facetService: EbFacetService,
    protected routingService: RoutingService
  ) {}

  private _uniqueFacets: EbFacet[]

  @Input('facets')
  set facets(facets: EbFacet[]) {
    this._uniqueFacets = EbFacetService.getUniqueFacets(facets)
    if (this._uniqueFacets) {
      this.filterFacets = this._uniqueFacets
        .filter(facet => !facet.specialFacet)
        .map(facet => this.transformFacetToSelectConfig(facet))
      this.specialFacets = this._uniqueFacets
        .filter(facet => facet.specialFacet)
        .map(facet => this.transformFacetToSelectConfig(facet))
    }
  }
  specialFacets: EbSelectConfig[] = []
  filterFacets: EbSelectConfig[] = []

  @Input() numberOfResults
  @Input() freeTextSearch: string
  specialFacetExpanded = false

  ngOnInit() {
    if (
      (this.specialFacets?.length > 0 || this._uniqueFacets?.length > 0) &&
      this.numberOfResults === 0
    ) {
      this.facetService.setResetFiltersMessageVisibility(true)
      this.routingService.go(location.pathname, { queryParams: {}, replaceUrl: true })
    }
  }

  toggleSpecialFacet() {
    this.specialFacetExpanded = !this.specialFacetExpanded
  }

  shouldBeCollapsed(specialFacet: string) {
    return (
      this.elementRef.nativeElement?.querySelector(`[special_facet_wrapper="${specialFacet}"]`)
        ?.scrollHeight > 200
    )
  }

  applyFilter(facetCode: string, facetValueCode: string, currentlySelected = false) {
    const currentFacet = this._uniqueFacets.find(facet => facet.code === facetCode)
    let query = this.facetService.getQueryWithoutFacet(currentFacet)
    if (!currentlySelected && facetValueCode) {
      query += `:${facetCode}:${facetValueCode}`
    }
    this.facetService.setResetFiltersMessageVisibility(false)
    this.routingService.go(location.pathname, { queryParams: { query }, replaceUrl: true })
  }
  private transformFacetToSelectConfig(facet: EbFacet) {
    const { name: label } = facet
    const images = this.getImages(facet.values)
    const items = facet.values?.map(facetValue => ({
      text: facetValue.name,
      code: facetValue.code,
      selected: facetValue.selected
    }))
    return {
      code: facet.code,
      label,
      items,
      images,
      selectedValue: items.find(item => item.selected)
    } as EbSelectConfig
  }

  private getImages(facetValues: EbFacetValue[]): { [key: string]: MediaContainer } {
    const images = {}
    facetValues?.forEach(value => {
      if (value.imageUrl) {
        images[value.name] = { url: value.imageUrl }
      }
    })
    return images
  }

  getFacetTitle(selectConfig: EbSelectConfig): Observable<string> {
    const NON_BREAKING_SPACE = String.fromCharCode(160)
    const key = 'productVariantList.' + selectConfig?.code?.toLowerCase()
    const i18nToken = `[product:${key}]`
    return this.translationService
      .translate(key)
      .pipe(
        map(
          label =>
            ((label === i18nToken || label === NON_BREAKING_SPACE) && selectConfig.label) || label
        )
      )
  }

  resetSearch() {
    this.routingService.go(location.pathname, { queryParams: {} })
  }

  get uniqueFacets(): EbFacet[] {
    return this._uniqueFacets
  }

  set uniqueFacets(value: EbFacet[]) {
    this._uniqueFacets = value
  }
}
